import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { MessageCircle, Send, Eye, Lock, Check, X } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useConversationThread } from '@/hooks/useConversationThread';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { cn } from '@/lib/utils';
import { toast } from 'sonner';

const threadCopy = {
  en: {
    replyPrivately: 'Reply Privately',
    replyDesc: 'Your identity stays hidden unless you choose to reveal it.',
    placeholder: 'Write your reply…',
    send: 'Send',
    threadTitle: 'Private Thread',
    you: 'You',
    them: 'Sender',
    revealTitle: 'Would you like to reveal yourself?',
    revealDesc: 'Both sides must agree. No one sees your identity unless you both accept.',
    requestReveal: 'I\'m ready to reveal',
    waitingReveal: 'Waiting for the other person…',
    bothRevealed: '🎉 You\'re both revealed! You can now see each other.',
    matched: 'Connection Made!',
    matchDesc: 'You\'ve formed a connection. Your conversation history is saved.',
    connectionBadge: '🤝 Connection Badge Earned',
    close: 'Close',
    replyStarted: 'Thread started!',
  },
  es: {
    replyPrivately: 'Responder Privadamente',
    replyDesc: 'Tu identidad permanece oculta a menos que elijas revelarla.',
    placeholder: 'Escribe tu respuesta…',
    send: 'Enviar',
    threadTitle: 'Hilo Privado',
    you: 'Tú',
    them: 'Remitente',
    revealTitle: '¿Te gustaría revelarte?',
    revealDesc: 'Ambas partes deben aceptar. Nadie ve tu identidad a menos que ambos acepten.',
    requestReveal: 'Estoy listo para revelarme',
    waitingReveal: 'Esperando a la otra persona…',
    bothRevealed: '🎉 ¡Ambos se han revelado! Ahora pueden verse.',
    matched: '¡Conexión Hecha!',
    matchDesc: 'Han formado una conexión. Su historial de conversación está guardado.',
    connectionBadge: '🤝 Insignia de Conexión Ganada',
    close: 'Cerrar',
    replyStarted: '¡Hilo iniciado!',
  },
  fr: {
    replyPrivately: 'Répondre en Privé',
    replyDesc: 'Votre identité reste cachée sauf si vous choisissez de la révéler.',
    placeholder: 'Écrivez votre réponse…',
    send: 'Envoyer',
    threadTitle: 'Fil Privé',
    you: 'Vous',
    them: 'Expéditeur',
    revealTitle: 'Souhaitez-vous vous révéler ?',
    revealDesc: 'Les deux parties doivent accepter. Personne ne voit votre identité sauf si vous acceptez tous les deux.',
    requestReveal: 'Je suis prêt à me révéler',
    waitingReveal: 'En attente de l\'autre personne…',
    bothRevealed: '🎉 Vous êtes tous les deux révélés !',
    matched: 'Connexion Établie !',
    matchDesc: 'Vous avez formé une connexion. Votre historique est sauvegardé.',
    connectionBadge: '🤝 Badge de Connexion Obtenu',
    close: 'Fermer',
    replyStarted: 'Fil créé !',
  },
  pt: {
    replyPrivately: 'Responder Privadamente',
    replyDesc: 'Sua identidade permanece oculta a menos que você escolha revelá-la.',
    placeholder: 'Escreva sua resposta…',
    send: 'Enviar',
    threadTitle: 'Conversa Privada',
    you: 'Você',
    them: 'Remetente',
    revealTitle: 'Gostaria de se revelar?',
    revealDesc: 'Ambos os lados devem concordar. Ninguém vê sua identidade a menos que ambos aceitem.',
    requestReveal: 'Estou pronto para me revelar',
    waitingReveal: 'Aguardando a outra pessoa…',
    bothRevealed: '🎉 Vocês dois se revelaram!',
    matched: 'Conexão Feita!',
    matchDesc: 'Vocês formaram uma conexão. Seu histórico está salvo.',
    connectionBadge: '🤝 Emblema de Conexão Conquistado',
    close: 'Fechar',
    replyStarted: 'Conversa iniciada!',
  },
  ar: {
    replyPrivately: 'الرد بخصوصية',
    replyDesc: 'هويتك تبقى مخفية إلا إذا اخترت الكشف عنها.',
    placeholder: 'اكتب ردك…',
    send: 'إرسال',
    threadTitle: 'محادثة خاصة',
    you: 'أنت',
    them: 'المرسل',
    revealTitle: 'هل تريد الكشف عن هويتك؟',
    revealDesc: 'يجب أن يوافق الطرفان. لا أحد يرى هويتك إلا إذا وافق كلاكما.',
    requestReveal: 'أنا مستعد للكشف',
    waitingReveal: 'في انتظار الشخص الآخر…',
    bothRevealed: '🎉 تم الكشف عن كليكما!',
    matched: 'تم الاتصال!',
    matchDesc: 'لقد أنشأتم اتصالاً. محادثتكم محفوظة.',
    connectionBadge: '🤝 شارة الاتصال',
    close: 'إغلاق',
    replyStarted: 'بدأت المحادثة!',
  },
  ja: {
    replyPrivately: 'プライベートに返信',
    replyDesc: 'あなたの身元は、公開を選択しない限り隠されます。',
    placeholder: '返信を書いてください…',
    send: '送信',
    threadTitle: 'プライベートスレッド',
    you: 'あなた',
    them: '送信者',
    revealTitle: '自分を明かしますか？',
    revealDesc: '両者が同意する必要があります。',
    requestReveal: '明かす準備ができました',
    waitingReveal: '相手を待っています…',
    bothRevealed: '🎉 お互いが明かされました！',
    matched: 'つながりが生まれました！',
    matchDesc: 'つながりが形成されました。会話履歴は保存されています。',
    connectionBadge: '🤝 つながりバッジ獲得',
    close: '閉じる',
    replyStarted: 'スレッドが開始されました！',
  },
  ko: {
    replyPrivately: '비공개로 답장',
    replyDesc: '공개를 선택하지 않는 한 신원이 숨겨집니다.',
    placeholder: '답장을 작성하세요…',
    send: '보내기',
    threadTitle: '비공개 대화',
    you: '나',
    them: '보낸 사람',
    revealTitle: '자신을 공개하시겠습니까?',
    revealDesc: '양측 모두 동의해야 합니다.',
    requestReveal: '공개할 준비가 되었습니다',
    waitingReveal: '상대방을 기다리는 중…',
    bothRevealed: '🎉 서로 공개되었습니다!',
    matched: '연결이 되었습니다!',
    matchDesc: '연결이 형성되었습니다. 대화 기록이 저장됩니다.',
    connectionBadge: '🤝 연결 배지 획득',
    close: '닫기',
    replyStarted: '대화가 시작되었습니다!',
  },
  id: {
    replyPrivately: 'Balas Secara Pribadi',
    replyDesc: 'Identitas Anda tetap tersembunyi kecuali Anda memilih untuk mengungkapkannya.',
    placeholder: 'Tulis balasan Anda…',
    send: 'Kirim',
    threadTitle: 'Utas Pribadi',
    you: 'Anda',
    them: 'Pengirim',
    revealTitle: 'Ingin mengungkapkan diri Anda?',
    revealDesc: 'Kedua belah pihak harus setuju.',
    requestReveal: 'Saya siap mengungkapkan',
    waitingReveal: 'Menunggu orang lain…',
    bothRevealed: '🎉 Kalian berdua terungkap!',
    matched: 'Koneksi Terbentuk!',
    matchDesc: 'Kalian membentuk koneksi. Riwayat percakapan tersimpan.',
    connectionBadge: '🤝 Lencana Koneksi Diperoleh',
    close: 'Tutup',
    replyStarted: 'Utas dimulai!',
  },
};

interface AnonymousReplyThreadProps {
  announcementId: string;
  isOwner: boolean;
}

export function AnonymousReplyThread({ announcementId, isOwner }: AnonymousReplyThreadProps) {
  const { language } = useLanguage();
  const c = lc(threadCopy, language);
  const [isOpen, setIsOpen] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const {
    thread,
    messages,
    revealRequest,
    isSending,
    startThread,
    sendMessage,
    requestReveal,
  } = useConversationThread(announcementId, isOwner);

  const isRevealed = thread?.status === 'revealed';
  const myRole = isOwner ? 'owner' : 'participant';
  const myRevealAccepted = isOwner ? revealRequest?.owner_accepted : revealRequest?.participant_accepted;
  const otherRevealAccepted = isOwner ? revealRequest?.participant_accepted : revealRequest?.owner_accepted;

  const handleSend = async () => {
    if (!newMessage.trim()) return;
    if (!thread) {
      const result = await startThread(newMessage);
      if (result) {
        const isFallback = (result as any)?.fallback === true;
        toast.success(isFallback ? 'Your reply has been sent privately.' : c.replyStarted);
        setNewMessage('');
      } else {
        toast.error('Could not send your reply right now. Please check your connection and try again.');
      }
    } else {
      const ok = await sendMessage(newMessage);
      if (ok) {
        setNewMessage('');
      } else {
        toast.error('Could not send message. Please try again.');
      }
    }
  };

  const handleReveal = async () => {
    const ok = await requestReveal();
    if (ok) toast.success(myRevealAccepted ? c.waitingReveal : c.requestReveal);
  };

  if (!isOpen) {
    return (
      <div className="w-full max-w-md mx-auto space-y-3">
        <button
          onClick={() => setIsOpen(true)}
          className="w-full flex flex-col items-center gap-2 px-6 py-5 rounded-xl border-2 border-dashed border-primary/40 bg-primary/5 hover:bg-primary/10 hover:border-primary/60 transition-all text-foreground"
        >
          <div className="flex items-center gap-3">
            <MessageCircle className="h-5 w-5 text-primary" />
            <span className="font-semibold text-sm">Want to answer this here?</span>
            {thread && messages.length > 0 && (
              <span className="ml-1 inline-flex h-5 w-5 items-center justify-center rounded-full bg-primary text-primary-foreground text-xs">
                {messages.length}
              </span>
            )}
          </div>
          <p className="text-xs text-muted-foreground">Replying here keeps the moment together.</p>
        </button>
        <p className="text-[10px] text-center text-muted-foreground/70">
          Your reply will appear privately in the sender's dashboard.
        </p>
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="w-full max-w-md mx-auto bg-card border border-border rounded-xl shadow-lg overflow-hidden"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-border bg-muted/30">
        <div className="flex items-center gap-2">
          <Lock className="h-4 w-4 text-muted-foreground" />
          <h3 className="text-sm font-semibold text-foreground">{c.threadTitle}</h3>
        </div>
        <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setIsOpen(false)}>
          <X className="h-4 w-4" />
        </Button>
      </div>

      {/* Privacy note */}
      {!thread && (
        <div className="px-4 py-3 bg-primary/5 border-b border-border">
          <p className="text-xs text-muted-foreground">{c.replyDesc}</p>
        </div>
      )}

      {/* Messages */}
      {messages.length > 0 && (
        <div className="max-h-64 overflow-y-auto p-4 space-y-3">
          {messages.map((msg) => {
            const isMe = msg.sender_role === myRole;
            return (
              <motion.div
                key={msg.id}
                initial={{ opacity: 0, y: 5 }}
                animate={{ opacity: 1, y: 0 }}
                className={cn('flex', isMe ? 'justify-end' : 'justify-start')}
              >
                <div
                  className={cn(
                    'max-w-[80%] rounded-2xl px-4 py-2 text-sm',
                    isMe
                      ? 'bg-primary text-primary-foreground rounded-br-md'
                      : 'bg-muted text-foreground rounded-bl-md'
                  )}
                >
                  <p className="text-[10px] opacity-70 mb-0.5">
                    {isMe ? c.you : c.them}
                  </p>
                  <p>{msg.message}</p>
                </div>
              </motion.div>
            );
          })}
        </div>
      )}

      {/* Reveal section — shown after 3+ messages */}
      {thread && messages.length >= 3 && !isRevealed && (
        <div className="px-4 py-3 border-t border-border bg-accent/10">
          <div className="text-center space-y-2">
            <p className="text-xs font-medium text-foreground">{c.revealTitle}</p>
            <p className="text-[10px] text-muted-foreground">{c.revealDesc}</p>
            {myRevealAccepted ? (
              <div className="flex items-center justify-center gap-1 text-xs text-muted-foreground">
                <Check className="h-3 w-3 text-primary" />
                {c.waitingReveal}
              </div>
            ) : (
              <Button size="sm" variant="outline" onClick={handleReveal} className="gap-1.5 text-xs">
                <Eye className="h-3 w-3" />
                {c.requestReveal}
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Match banner */}
      {isRevealed && (
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          className="px-4 py-4 border-t border-border bg-gradient-to-r from-primary/10 to-accent/10 text-center space-y-1"
        >
          <p className="text-sm font-bold text-foreground">{c.matched}</p>
          <p className="text-xs text-muted-foreground">{c.matchDesc}</p>
          <p className="text-xs text-primary font-medium">{c.connectionBadge}</p>
        </motion.div>
      )}

      {/* Input */}
      <div className="p-3 border-t border-border flex items-end gap-2">
        <Textarea
          value={newMessage}
          onChange={(e) => setNewMessage(e.target.value)}
          placeholder={c.placeholder}
          className="min-h-[40px] max-h-[100px] text-sm resize-none"
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
        />
        <Button
          size="icon"
          onClick={handleSend}
          disabled={!newMessage.trim() || isSending}
          className="shrink-0 h-10 w-10"
        >
          <Send className="h-4 w-4" />
        </Button>
      </div>
    </motion.div>
  );
}
